import { TaskTemplate } from "@/lib/vibrato-api"
import { Button } from "flowbite-react"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { VibratoClientContext } from "./contexts"
import { AlignLeft, Plus } from "iconoir-react"

// Card component for mobile view
const TaskTemplateCard: React.FC<{
  template: TaskTemplate
  onCreateCall: (uuid: string) => void
}> = ({ template, onCreateCall }) => {
  return (
    <div className="p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors">
      <div className="flex justify-between items-start mb-3">
        <div className="flex flex-col">
          <span className="text-xs text-gray-500 uppercase tracking-wider mb-1">Template Name</span>
          <div className="text-lg font-medium text-gray-900">{template.taskName}</div>
        </div>
      </div>
      <div className="mb-4">
        <span className="text-xs text-gray-500 uppercase tracking-wider mb-1 block">Description</span>
        <p className="text-sm text-gray-600 line-clamp-3">{template.taskDescription}</p>
      </div>
      <div className="flex items-center justify-end gap-2">
        <Link to={`/task-templates/${template.uuid}`}>
          <Button size="xs" pill color="light" className="font-medium">
            Edit
          </Button>
        </Link>
        <Button
          size="xs"
          pill
          onClick={() => onCreateCall(template.uuid)}
          className="w-fit whitespace-nowrap bg-linear-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 border-0 font-medium"
        >
          Create Call
        </Button>
      </div>
    </div>
  )
}

const TaskTemplatesList: React.FC = () => {
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([])
  const vibratoClient = useContext(VibratoClientContext)
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    document.title = "Vibrato - Task Templates"
  }, [])

  useEffect(() => {
    const fetchTaskTemplates = async () => {
      if (vibratoClient) {
        try {
          setIsLoading(true)
          const templates = await vibratoClient.getTaskTemplates()
          setTaskTemplates(templates)
        } catch (error) {
          console.error("Failed to fetch task templates:", error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    fetchTaskTemplates()
  }, [vibratoClient])

  const createCall = useCallback(
    async (uuid: string) => {
      const call = await vibratoClient.createCallFromTaskTemplate({
        taskTemplateUuid: uuid
      })

      navigate(`/calls/${call.id}/edit`)
    },
    [vibratoClient, navigate]
  )

  return (
    <div className="h-full w-full p-6 md:p-8 flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-xl">
            <AlignLeft width="24" className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Task Templates</h1>
            <p className="text-gray-600">Create and manage reusable task configurations</p>
          </div>
        </div>

        <Link to="/task-templates/new">
          <button className="px-5 py-2.5 bg-linear-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 whitespace-nowrap">
            <Plus className="w-5 h-5" />
            <span className="hidden md:inline">Create Template</span>
            <span className="md:hidden">Create</span>
          </button>
        </Link>
      </div>

      <div className="bg-slate-50 p-4 md:p-6 rounded-xl border border-slate-200">
        <p className="text-sm md:text-base text-slate-700">
          Task templates are reusable task configurations that can be used to create calls quickly. Use them to define
          calls that are commonly used in your organization, such as follow-up calls or reminders.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64 bg-white rounded-lg shadow-md">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4" />
            <p className="text-gray-600">Loading templates...</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col">
          {taskTemplates.length > 0 ? (
            <div className="w-full">
              {/* Mobile card view */}
              <div className="md:hidden overflow-hidden  bg-white shadow-md rounded-2xl">
                <div className="bg-gray-50 px-4 py-3 border-b border-t border-gray-200 ">
                  <div className="flex text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="w-2/5">Template Name</div>
                  </div>
                </div>
                {taskTemplates.map((template) => (
                  <TaskTemplateCard key={template.uuid} template={template} onCreateCall={createCall} />
                ))}
              </div>

              {/* Desktop table view */}
              <div className="hidden md:block overflow-auto rounded-lg">
                <table className="max-h-full w-full text-sm text-left text-gray-500 dark:text-gray-400 divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Template Name
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Description
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {taskTemplates.map((template) => (
                      <tr key={template.uuid} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-base font-medium text-gray-900">{template.taskName}</div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-600 line-clamp-2">{template.taskDescription}</p>
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          <div className="flex gap-2 justify-end">
                            <Link to={`/task-templates/${template.uuid}`}>
                              <Button size="xs" pill color="light" className="font-medium">
                                Edit
                              </Button>
                            </Link>
                            <Button
                              size="xs"
                              pill
                              onClick={() => createCall(template.uuid)}
                              className="w-fit whitespace-nowrap bg-linear-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 border-0 font-medium"
                            >
                              Create Call
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <EmptyState />
          )}
        </div>
      )}
    </div>
  )
}

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center h-fit p-6">
    <div className="flex flex-col items-center justify-center my-4">
      <div className="p-4 bg-purple-100 rounded-full mb-4">
        <AlignLeft width="32" height="32" className="text-purple-600" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No task templates yet</h3>
      <p className="text-gray-600 text-center max-w-md mb-6">
        Create your first task template to define reusable tasks for making phone calls with AI.
      </p>
      <Link to="/task-templates/new">
        <button className="px-6 py-3 bg-linear-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2">
          <Plus className="w-5 h-5" />
          Create Task Template
        </button>
      </Link>
    </div>
  </div>
)

export default TaskTemplatesList
